import { Box, Choicebox, Divider, FormGroup, FormSection } from '@ksyos/design-system';
import type { OsasConsultData } from '@common/validation/customControls/osas/osasConsultSchemas/types';
import type { OsasTreatmentData } from '@common/validation/customControls/osas/osasTreatmentChoice/types';
import type { ValidationErrors } from '@frontend/utils';
import { treatmentNames } from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import { treatmentOptionsLabels } from '@common/validation/customControls/osas/osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import { FormValidationMessage } from '@frontend/components/elements/FormValidationMessage';
import type { TreatmentOptionsKeys } from './utils';
import { TextAreaFormGroup } from '../../../../processActivityFormGroupFields';
import { osasYesChosenTreatmentInitialData } from '../../osasConsultData';
import { CpapSetting } from './CpapSetting';
import { LifestyleFormGroup } from './LifestyleFormGroup';
import { ReferFormGroup } from './ReferFormGroup';
import { TreatmentFollowUps } from './TreatmentFollowUps';
import { TreatmentRadioGroup } from './TreatmentRadioGroup';
import { TreatmentTextInput } from './TreatmentTextInput';

type TreatmentFormSectionProps = {
    data: OsasTreatmentData;
    errors: ValidationErrors;
    onChange: (data: Partial<OsasTreatmentData>, validateKeys?: string | string[] | undefined) => void;
};

export function TreatmentFormSection(props: Readonly<TreatmentFormSectionProps>) {
    const { data, errors, onChange } = props;

    const treatmentOptionsToRender = treatmentNames.map((treatmentName) => (
        <Choicebox
            checked={Boolean(data.treatment[treatmentName])}
            key={treatmentName}
            label={treatmentOptionsLabels[treatmentName]}
            onChange={async (e) => {
                let updatedData: Partial<OsasConsultData> = {
                    treatment: { ...data.treatment, [treatmentName]: e.target.checked },
                };
                if (!e.target.checked) {
                    updatedData.treatmentFollowUp = { ...data.treatmentFollowUp, [treatmentName]: [] };
                }
                if (!e.target.checked && treatmentName === 'CPAP') {
                    updatedData = { ...updatedData, cpapSetting: null, cpapSettingText: null };
                }
                if (!e.target.checked && treatmentName === 'refer') {
                    updatedData = { ...updatedData, refer: osasYesChosenTreatmentInitialData.refer };
                }

                if (
                    (!e.target.checked && treatmentName === 'lifestyle') ||
                    updatedData.treatment?.CPAP ||
                    updatedData.treatment?.MRA ||
                    updatedData.treatment?.SPT ||
                    updatedData.treatment?.refer
                ) {
                    updatedData = {
                        ...updatedData,
                        shouldHaveFollowUpConsult: null,
                        shouldHaveFollowingExamination: null,
                        taskFollowingExaminationOrConsultWeeks: null,
                    };
                }

                onChange({ ...updatedData }, 'treatment');
            }}
        />
    ));

    const treatmentOptionInformationToRender = treatmentNames.map((treatmentName) => {
        if (!data.treatment[treatmentName]) {
            return null;
        }

        return (
            <FormSection
                isBorderless={true}
                key={treatmentName}
                title={treatmentName === 'refer' ? 'Doorverwijzen aan' : treatmentOptionsLabels[treatmentName]}
            >
                {getTreatmentInformation(treatmentName)}
                {treatmentName === 'CPAP' && (
                    <CpapSetting
                        data={data}
                        errors={errors}
                        onChange={onChange}
                    />
                )}
            </FormSection>
        );
    });

    return (
        <>
            <FormGroup
                hasErrors={errors.has('treatment')}
                isRequired={true}
                label={'Behandeling'}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    gap={3}
                    key={'treatment-checkboxes'}
                >
                    {treatmentOptionsToRender}
                </Box>
                {!Object.values(data.treatment).some((value) => Boolean(value)) && (
                    <FormValidationMessage error={errors.get('treatment')} />
                )}
            </FormGroup>

            {treatmentOptionInformationToRender}
            <Divider spacing="large" />

            <TextAreaFormGroup
                fieldKey="remarkTreatment"
                label="Toelichting behandeling"
            />
        </>
    );

    function getTreatmentInformation(treatmentName: TreatmentOptionsKeys) {
        if (treatmentName === 'other') {
            return (
                <TreatmentTextInput
                    data={data}
                    errors={errors}
                    onChange={onChange}
                    treatmentName={treatmentName}
                />
            );
        } else if (treatmentName === 'lifestyle') {
            return (
                <LifestyleFormGroup
                    data={data}
                    errors={errors}
                    onChange={onChange}
                />
            );
        }

        if (treatmentName === 'refer') {
            return <ReferFormGroup />;
        }

        return (
            <>
                <TreatmentRadioGroup
                    data={data}
                    errors={errors}
                    onChange={onChange}
                    treatmentName={treatmentName}
                />

                <TreatmentFollowUps
                    data={data}
                    onChange={onChange}
                    treatmentName={treatmentName}
                />
            </>
        );
    }
}
